// encounter.ClinicalNote carries a small HTML subset; legacy notes are still plain text,
// so every read normalises instead of assuming a shape.

/**
 * Nora notes are whitespace-significant (3-space-indented sub-bullets under '- ' bullets);
 * HTML collapses leading whitespace, so protect it or the hierarchy is lost on render.
 */
function preserveIndent(line: string): string {
  return line.replace(/^[ \t]+/, run => '&nbsp;'.repeat(run.replace(/\t/g, '    ').length));
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const CLINICAL_NOTE_TAG = /<(p|br|ul|ol|li|strong|em|u|b|i)\b[^>]*>/i;

/**
 * True when the value already carries our HTML subset. Plain text with stray
 * angle brackets ("temp <38", "vitamin <b12") does not match - \b guards it.
 */
function isClinicalNoteHtml(value: string): boolean {
  return !!value && CLINICAL_NOTE_TAG.test(value);
}

/**
 * Shared TinyMCE init - spread it and add a placeholder. valid_elements mirrors CLINICAL_NOTE_TAG
 * with no attributes, so pasted styles, images and tables are dropped rather than persisted.
 */
export const clinicalNoteEditorInit = {
  base_url: '/tinymce',
  suffix: '.min',
  plugins: 'lists paste',
  toolbar: 'bold italic underline | bullist numlist | undo redo',
  // '#p' keeps empty paragraphs padded: setting valid_elements drops TinyMCE's default
  // paddEmpty rule, and an unpadded <p></p> renders as no line at all.
  valid_elements: '#p,br,ul,ol,li,strong/b,em/i,u',
  // Store characters as typed: the default named encoding would persist "38&deg;C", which
  // plain-text readers (search, copy, Narrative) never decode.
  entity_encoding: 'raw',
  paste_data_images: false,
  // TinyMCE's default underline is a styled <span>, which valid_elements drops and
  // Angular's [innerHTML] sanitizer strips anyway - emit <u> like bold emits <strong>.
  formats: { underline: { inline: 'u', remove: 'all' } },
  // The iframe never sees page fonts; 'default' keeps TinyMCE's spacing. Root-absolute because
  // TinyMCE resolves relative paths against the URL the app was opened on, not <base href>.
  content_css: ['default', '/assets/figtree.css'],
  menubar: false,
  statusbar: false,
  branding: false,
  browser_spellcheck: true,
  contextmenu: false,
};

/**
 * Legacy plain text -> HTML. Idempotent: HTML passes through untouched.
 * Escapes first - clinical notes legitimately contain < and > (e.g. "sats >95%").
 */
export function clinicalNoteToHtml(value: string): string {
  if (!value) {
    return '';
  }
  if (isClinicalNoteHtml(value)) {
    return value;
  }
  const escaped = escapeHtml(value);
  return escaped
    .split(/\r\n|\r|\n/)
    .map(line => `<p>${preserveIndent(line) || '<br>'}</p>`)
    .join('');
}

/**
 * HTML -> readable text for search, copy, previews and telemetry-free displays.
 * Block-aware: paragraphs and list items become newlines rather than running together.
 */
export function clinicalNoteToPlainText(value: string): string {
  if (!value) {
    return '';
  }
  if (!isClinicalNoteHtml(value)) {
    return value;
  }
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '\n- ')
    .replace(/<\/(p|div|ul|ol|h[1-6])\s*>/gi, '\n') // not </li>: <li> already emits the break
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;|\u00a0/gi, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, `'`)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * An editor that was opened and cleared yields <p></p> / <p><br></p> / <p>&nbsp;</p>.
 * None of those is a captured note.
 */
export function isBlankClinicalNote(value: string): boolean {
  return clinicalNoteToPlainText(value).replace(/\s/g, '').length === 0;
}

/** Joins an appended fragment onto an existing note without producing orphaned markup. */
export function appendToClinicalNote(existing: string, addition: string): string {
  if (!addition) {
    return existing || '';
  }
  const additionHtml = clinicalNoteToHtml(addition);
  return isBlankClinicalNote(existing) ? additionHtml : `${clinicalNoteToHtml(existing)}${additionHtml}`;
}

/**
 * Nora wraps the note in a Quill delta; the text is whitespace-significant, so hand it to
 * clinicalNoteToHtml. Every string op is kept, and the trailing newline Quill appends is dropped.
 */
export function quillDeltaToClinicalNoteHtml(raw: string | { ops?: any[] }): string {
  let delta: any = raw;
  if (typeof raw === 'string') {
    try {
      delta = JSON.parse(raw);
    } catch {
      return clinicalNoteToHtml(raw);
    }
  }
  if (!Array.isArray(delta?.ops)) {
    return clinicalNoteToHtml(typeof raw === 'string' ? raw : '');
  }
  const text = delta.ops.map((op: any) => (typeof op?.insert === 'string' ? op.insert : '')).join('');
  return clinicalNoteToHtml(text.replace(/\n$/, ''));
}
